import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import '../../styles/Popover.css';

export type PopoverPlacement =
  | 'top'
  | 'topLeft'
  | 'topRight'
  | 'bottom'
  | 'bottomLeft'
  | 'bottomRight'
  | 'left'
  | 'right';

export interface PopoverProps {
  /** 触发器元素 */
  children: React.ReactNode;
  /** 弹出内容 */
  content: React.ReactNode;
  /** 受控模式：是否显示 */
  open?: boolean;
  /** 非受控模式：默认是否显示 */
  defaultOpen?: boolean;
  /** 开关状态变化回调 */
  onOpenChange?: (open: boolean) => void;
  /** 弹出位置 */
  placement?: PopoverPlacement;
  /** 触发方式 */
  trigger?: 'click' | 'hover';
  /** 触发器容器类名 */
  className?: string;
  /** 弹出内容类名 */
  contentClassName?: string;
  /** 与触发器的间距 */
  offset?: number;
  /** 是否禁用 */
  disabled?: boolean;
}

/**
 * 计算弹出层位置
 */
function calculatePosition(
  triggerRect: DOMRect,
  contentRect: DOMRect,
  placement: PopoverPlacement,
  offset: number
): { top: number; left: number } {
  const { top, left, bottom, right, width, height } = triggerRect;
  const contentWidth = contentRect.width;
  const contentHeight = contentRect.height;

  let resultTop = 0;
  let resultLeft = 0;

  switch (placement) {
    case 'top':
      resultTop = top - contentHeight - offset;
      resultLeft = left + width / 2 - contentWidth / 2;
      break;
    case 'topLeft':
      resultTop = top - contentHeight - offset;
      resultLeft = left;
      break;
    case 'topRight':
      resultTop = top - contentHeight - offset;
      resultLeft = right - contentWidth;
      break;
    case 'bottom':
      resultTop = bottom + offset;
      resultLeft = left + width / 2 - contentWidth / 2;
      break;
    case 'bottomLeft':
      resultTop = bottom + offset;
      resultLeft = left;
      break;
    case 'bottomRight':
      resultTop = bottom + offset;
      resultLeft = right - contentWidth;
      break;
    case 'left':
      resultTop = top + height / 2 - contentHeight / 2;
      resultLeft = left - contentWidth - offset;
      break;
    case 'right':
      resultTop = top + height / 2 - contentHeight / 2;
      resultLeft = right + offset;
      break;
    default:
      resultTop = bottom + offset;
      resultLeft = left;
  }

  // 边界检测：确保不超出视口
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // 水平方向边界
  if (resultLeft < 8) {
    resultLeft = 8;
  } else if (resultLeft + contentWidth > viewportWidth - 8) {
    resultLeft = viewportWidth - contentWidth - 8;
  }

  // 垂直方向边界
  if (resultTop < 8) {
    resultTop = 8;
  } else if (resultTop + contentHeight > viewportHeight - 8) {
    resultTop = viewportHeight - contentHeight - 8;
  }

  return { top: resultTop, left: resultLeft };
}

/**
 * 自定义 Popover 组件
 */
export const Popover: React.FC<PopoverProps> = ({
  children,
  content,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  placement = 'bottom',
  trigger = 'click',
  className,
  contentClassName,
  offset = 8,
  disabled = false,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [isPositioned, setIsPositioned] = useState(false);

  // 切换开关状态
  const setOpen = useCallback(
    (newOpen: boolean) => {
      if (disabled) return;
      if (!isControlled) {
        setInternalOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [disabled, isControlled, onOpenChange]
  );

  // 计算位置
  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !contentRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    // 使用 offsetWidth/offsetHeight 而不是 getBoundingClientRect
    // 因为 getBoundingClientRect 会受 transform scale 影响
    const contentRect = {
      width: contentRef.current.offsetWidth,
      height: contentRef.current.offsetHeight,
    } as DOMRect;
    const newPosition = calculatePosition(triggerRect, contentRect, placement, offset);

    setPosition(newPosition);
    setIsPositioned(true);
  }, [placement, offset]);

  // 打开时计算位置
  useEffect(() => {
    if (open) {
      // 重置位置状态
      setPosition(null);
      setIsPositioned(false);
      // 延迟一帧，等待 content 渲染完成
      requestAnimationFrame(() => {
        updatePosition();
      });
    } else {
      setIsPositioned(false);
      setPosition(null);
    }
  }, [open, updatePosition]);

  // 监听内容尺寸变化，重新计算位置
  useEffect(() => {
    if (!open || !contentRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      updatePosition();
    });

    resizeObserver.observe(contentRef.current);
    return () => resizeObserver.disconnect();
  }, [open, updatePosition]);

  // 窗口 resize 时重新计算位置
  useEffect(() => {
    if (!open) return;

    const handleResize = () => updatePosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [open, updatePosition]);

  // 点击外部关闭
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        contentRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    // 使用 mousedown 而不是 click，响应更快
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, setOpen]);

  // ESC 键关闭
  useEffect(() => {
    if (!open) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, setOpen]);

  // 点击触发
  const handleClick = () => {
    if (trigger === 'click') {
      setOpen(!open);
    }
  };

  // Hover 触发
  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      setOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (trigger === 'hover') {
      setOpen(false);
    }
  };

  return (
    <>
      <div
        ref={triggerRef}
        className={`popover-trigger ${className || ''}`}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>

      {open &&
        createPortal(
          <div
            ref={contentRef}
            className={`popover-content ${contentClassName || ''} ${isPositioned ? 'popover-visible' : ''}`}
            data-placement={placement}
            style={{
              position: 'fixed',
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: isPositioned ? 'visible' : 'hidden',
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};

export default Popover;
